import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  COUNTRIES,
  COUNTRY_AREAS,
  MULTIZONE_ENABLED,
  areaColor,
  areasInCountry,
  countryOf,
} from "./constants";
import { useLanguage } from "../../i18n/LanguageProvider";
import { track } from "../../utils/track";
import { FlagDK, FlagFI, FlagNO, FlagSE } from "../icons";
import { CURRENCIES, Currency, useCurrency } from "../../currency/CurrencyProvider";
import "./AreaPicker.css";

interface AreaPickerProps {
  selectedArea: string;
  onChange: (area: string) => void;
  // Forecast model: null = standard, "pro" = the Pro forecast (Premium).
  model?: string | null;
  onModelChange?: (model: string | null) => void;
  canViewPro?: boolean;
  onUpgrade?: () => void; // where a locked Pro option leads
  showCurrency?: boolean;
}

const CURRENCY_FLAG: Record<Currency, string> = { SEK: "SE", EUR: "FI", NOK: "NO", DKK: "DK" };

const FLAGS: Record<string, (p: { size?: number | string }) => JSX.Element> = {
  SE: FlagSE,
  NO: FlagNO,
  FI: FlagFI,
  DK: FlagDK,
};

const useDark = () => {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute("data-bs-theme") === "dark",
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.getAttribute("data-bs-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);
  return dark;
};

// Closes a popover on outside click or Escape.
const useDismiss = (open: boolean, close: () => void) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
};

// Bidding-zone selector: one pill with a country menu (multi-country preview),
// the country's zones as a segmented control whose highlight slides to the
// selected zone in that zone's colour, and a forecast-model menu. Used in the
// sticky top bar (home) and the admin forecast-validation chart.
export const AreaPicker = ({
  selectedArea, onChange, model, onModelChange, canViewPro = false, onUpgrade, showCurrency = false,
}: AreaPickerProps) => {
  const { t } = useLanguage();
  const dark = useDark();
  const country = countryOf(selectedArea);
  const zones = areasInCountry(selectedArea);

  const [countryOpen, setCountryOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const closeCurrency = useCallback(() => setCurrencyOpen(false), []);
  const currencyRef = useDismiss(currencyOpen, closeCurrency);
  const { currency, selected: selectedCurrency, setCurrency, ratesDate } = useCurrency();
  const closeCountry = useCallback(() => setCountryOpen(false), []);
  const closeModel = useCallback(() => setModelOpen(false), []);
  const countryRef = useDismiss(countryOpen, closeCountry);
  const modelRef = useDismiss(modelOpen, closeModel);

  // Sliding highlight under the selected zone.
  const zonesRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const btn = zonesRef.current?.querySelector<HTMLButtonElement>(`[data-zone="${selectedArea}"]`);
      setIndicator(btn ? { left: btn.offsetLeft, width: btn.offsetWidth } : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (zonesRef.current) ro.observe(zonesRef.current);
    return () => ro.disconnect();
  }, [selectedArea, country]);

  const Flag = FLAGS[country];
  const showModel = !!onModelChange;
  const activeModel = canViewPro ? model ?? null : null;

  return (
    <div className="loc-switch">
      {MULTIZONE_ENABLED && (
        <div className="loc-country" ref={countryRef}>
          <button
            type="button"
            className="loc-country__btn"
            onClick={() => { setCountryOpen((o) => !o); setModelOpen(false); setCurrencyOpen(false); }}
            aria-haspopup="listbox"
            aria-expanded={countryOpen}
            title={t(`country.${country}`)}
          >
            <span className="loc-flag"><Flag size={18} /></span>
            <span className="loc-country__name">{t(`country.${country}`)}</span>
            <svg className="loc-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </button>
          {countryOpen && (
            <div className="loc-pop" role="listbox" aria-label={t("navbar.country")}>
              {COUNTRIES.map((c) => {
                const F = FLAGS[c];
                return (
                  <button
                    key={c}
                    type="button"
                    role="option"
                    aria-selected={c === country}
                    className={`loc-pop__item ${c === country ? "is-active" : ""}`}
                    onClick={() => {
                      setCountryOpen(false);
                      if (c !== country) { onChange(COUNTRY_AREAS[c][0]); track("area_toggle", c); }
                    }}
                  >
                    <span className="loc-flag"><F size={20} /></span>
                    <span className="loc-pop__name">{t(`country.${c}`)}</span>
                    <span className="loc-pop__zones">
                      {COUNTRY_AREAS[c].map((z) => (
                        <span key={z} className="loc-pop__dot" style={{ background: areaColor(z, dark) }} title={z} />
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="loc-zones" role="radiogroup" ref={zonesRef}>
        {indicator && (
          <span
            className="loc-zones__indicator"
            style={{ left: indicator.left, width: indicator.width, background: areaColor(selectedArea, dark) }}
            aria-hidden="true"
          />
        )}
        {zones.map((area) => {
          const active = area === selectedArea;
          return (
            <button
              key={area}
              type="button"
              role="radio"
              aria-checked={active}
              data-zone={area}
              className={`loc-zone ${active ? "is-active" : ""}`}
              onClick={() => { if (!active) { onChange(area); track("area_toggle", area); } }}
            >
              <span className="loc-zone__dot" style={{ background: active ? "#fff" : areaColor(area, dark) }} />
              {area}
            </button>
          );
        })}
      </div>

      {showModel && (
        <div className="loc-model" ref={modelRef}>
          <button
            type="button"
            className={`loc-model__btn ${activeModel ? "is-pro" : ""}`}
            onClick={() => { setModelOpen((o) => !o); setCountryOpen(false); setCurrencyOpen(false); }}
            aria-haspopup="menu"
            aria-expanded={modelOpen}
            title={t(activeModel ? "home.model.pro" : "home.model.production")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg>
            <span className="loc-model__label">{activeModel ? "Pro" : t("home.model.short")}</span>
          </button>
          {modelOpen && (
            <div className="loc-pop loc-pop--right" role="menu">
              <div className="loc-pop__title">{t("home.model.title")}</div>
              {[null, "pro"].map((m) => {
                const locked = m === "pro" && !canViewPro;
                return (
                  <button
                    key={m ?? "standard"}
                    type="button"
                    role="menuitemradio"
                    aria-checked={activeModel === m}
                    className={`loc-pop__item loc-pop__item--model ${activeModel === m ? "is-active" : ""} ${locked ? "is-locked" : ""}`}
                    onClick={() => {
                      setModelOpen(false);
                      if (locked) {
                        track("cta_click", "pro_forecast_locked");
                        onUpgrade?.();
                      } else {
                        onModelChange?.(m);
                      }
                    }}
                  >
                    <span className="loc-pop__name">
                      {t(m ? "home.model.pro" : "home.model.production")}
                      {m && <span className="loc-badge">Premium</span>}
                    </span>
                    <span className="loc-pop__desc">
                      {t(m ? (locked ? "home.model.proLocked" : "home.model.proDesc") : "home.model.productionDesc")}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showCurrency && (
        <div className="loc-model" ref={currencyRef}>
          <button
            type="button"
            className="loc-model__btn loc-currency__btn"
            onClick={() => { setCurrencyOpen((o) => !o); setCountryOpen(false); setModelOpen(false); }}
            aria-haspopup="menu"
            aria-expanded={currencyOpen}
            title={t("currency.title")}
          >
            <span className="loc-currency__code">{currency}</span>
          </button>
          {currencyOpen && (
            <div className="loc-pop loc-pop--right loc-pop--narrow" role="menu">
              <div className="loc-pop__title">{t("currency.title")}</div>
              {CURRENCIES.map((c) => {
                const F = FLAGS[CURRENCY_FLAG[c]];
                return (
                  <button
                    key={c}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selectedCurrency === c}
                    className={`loc-pop__item ${selectedCurrency === c ? "is-active" : ""}`}
                    onClick={() => { setCurrency(c); setCurrencyOpen(false); }}
                  >
                    {c === "EUR" ? <span className="loc-eur">€</span> : <span className="loc-flag"><F size={20} /></span>}
                    <span className="loc-pop__name">{t(`currency.${c}`)}</span>
                    <span className="loc-pop__desc">{c === "EUR" ? "c/kWh" : `${c}/kWh`}</span>
                  </button>
                );
              })}
              {ratesDate && <div className="loc-pop__foot">{t("currency.source", { date: ratesDate })}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
